import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Search, Download, Eye, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BillingManagement() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users?limit=1000`, { headers });
      // Filtrar solo usuarios que tienen datos de facturación
      const usersWithBilling = response.data.users.filter(u => u.billing_info);
      setUsers(usersWithBilling);
    } catch (err) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const searchLower = search.toLowerCase();
    return (
      u.email?.toLowerCase().includes(searchLower) ||
      u.name?.toLowerCase().includes(searchLower) ||
      u.billing_info?.razon_social?.toLowerCase().includes(searchLower) ||
      u.billing_info?.rfc?.toLowerCase().includes(searchLower)
    );
  });

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowDetails(true);
  };

  const exportToCSV = () => {
    const headers = ['Email', 'Nombre', 'Razón Social', 'RFC', 'Régimen Fiscal', 'Uso CFDI', 'Email Facturación', 'Calle', 'Colonia', 'CP', 'Ciudad', 'Estado'];
    const rows = filteredUsers.map(u => [
      u.email || '',
      u.name || '',
      u.billing_info?.razon_social || '',
      u.billing_info?.rfc || '',
      u.billing_info?.regimen_fiscal || '',
      u.billing_info?.uso_cfdi || '',
      u.billing_info?.email_facturacion || '',
      u.billing_info?.calle || '',
      u.billing_info?.colonia || '',
      u.billing_info?.codigo_postal || '',
      u.billing_info?.ciudad || '',
      u.billing_info?.estado || ''
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `facturacion_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV exportado correctamente');
  };

  if (loading) {
    return <div className="p-8"><div className="animate-pulse text-muted-foreground">Cargando...</div></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-semibold tracking-tight">Datos de Facturación</h1>
          <p className="text-muted-foreground mt-1">Gestión de información fiscal de clientes</p>
        </div>
        <Button onClick={exportToCSV} disabled={filteredUsers.length === 0} className="gap-2">
          <Download className="h-4 w-4" />
          Exportar CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-sm text-muted-foreground">Usuarios con datos fiscales</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <FileText className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.billing_info?.rfc).length}</p>
                <p className="text-sm text-muted-foreground">Con RFC registrado</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.billing_info?.email_facturacion).length}</p>
                <p className="text-sm text-muted-foreground">Con email de facturación</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por email, nombre, razón social o RFC..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Usuarios ({filteredUsers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-20" />
              <p>No se encontraron usuarios con datos de facturación</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium text-sm">Usuario</th>
                    <th className="text-left p-3 font-medium text-sm">Razón Social</th>
                    <th className="text-left p-3 font-medium text-sm">RFC</th>
                    <th className="text-left p-3 font-medium text-sm">Email Facturación</th>
                    <th className="text-left p-3 font-medium text-sm">Estado</th>
                    <th className="text-right p-3 font-medium text-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b hover:bg-secondary/50 transition-colors">
                      <td className="p-3">
                        <div>
                          <p className="font-medium text-sm">{user.name || 'Sin nombre'}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </td>
                      <td className="p-3 text-sm">{user.billing_info?.razon_social || '-'}</td>
                      <td className="p-3">
                        <code className="text-xs bg-secondary px-2 py-1 rounded">
                          {user.billing_info?.rfc || '-'}
                        </code>
                      </td>
                      <td className="p-3 text-sm">{user.billing_info?.email_facturacion || '-'}</td>
                      <td className="p-3">
                        {user.billing_info?.ciudad && user.billing_info?.estado ? (
                          <span className="text-xs">{user.billing_info.ciudad}, {user.billing_info.estado}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(user)}
                          className="gap-2"
                        >
                          <Eye className="h-4 w-4" />
                          Ver Detalles
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Datos de Facturación</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6">
              {/* Usuario */}
              <div>
                <h3 className="font-semibold mb-3">Información del Usuario</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Nombre</p>
                    <p className="font-medium">{selectedUser.name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedUser.email}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Teléfono</p>
                    <p className="font-medium">{selectedUser.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Rol</p>
                    <Badge variant={selectedUser.role === 'admin' ? 'default' : 'secondary'}>
                      {selectedUser.role}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Datos Fiscales */}
              <div>
                <h3 className="font-semibold mb-3">Datos Fiscales</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Razón Social</p>
                    <p className="font-medium">{selectedUser.billing_info?.razon_social || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">RFC</p>
                    <code className="text-xs bg-secondary px-2 py-1 rounded font-mono">
                      {selectedUser.billing_info?.rfc || '-'}
                    </code>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Régimen Fiscal</p>
                    <p className="font-medium">{selectedUser.billing_info?.regimen_fiscal || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Uso CFDI</p>
                    <p className="font-medium">{selectedUser.billing_info?.uso_cfdi || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-muted-foreground">Email de Facturación</p>
                    <p className="font-medium">{selectedUser.billing_info?.email_facturacion || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Dirección Fiscal */}
              <div>
                <h3 className="font-semibold mb-3">Dirección Fiscal</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Calle</p>
                    <p className="font-medium">{selectedUser.billing_info?.calle || '-'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-muted-foreground">No. Ext</p>
                      <p className="font-medium">{selectedUser.billing_info?.numero_exterior || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">No. Int</p>
                      <p className="font-medium">{selectedUser.billing_info?.numero_interior || '-'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Colonia</p>
                    <p className="font-medium">{selectedUser.billing_info?.colonia || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Código Postal</p>
                    <p className="font-medium">{selectedUser.billing_info?.codigo_postal || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Ciudad</p>
                    <p className="font-medium">{selectedUser.billing_info?.ciudad || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Estado</p>
                    <p className="font-medium">{selectedUser.billing_info?.estado || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-muted-foreground">País</p>
                    <p className="font-medium">{selectedUser.billing_info?.pais || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
